import { useEffect, useRef, useCallback } from 'react';

interface UseIntersectionObserverProps {
  onIntersect: () => void;
  enabled?: boolean;
  threshold?: number;
  rootMargin?: string;
}

/**
 * Hook to observe intersection of an element with the viewport
 *
 * Uses the Intersection Observer API to detect when a target element
 * enters the viewport. Commonly used for infinite scroll triggers,
 * lazy loading, or visibility tracking.
 *
 * @param {UseIntersectionObserverProps} props - Hook configuration
 * @returns {React.RefObject} Ref object to attach to the target element
 *
 * @example
 * const observerRef = useIntersectionObserver({
 *   onIntersect: () => fetchNextPage(),
 *   enabled: hasNextPage,
 *   threshold: 1.0,
 * });
 *
 * return <div ref={observerRef}>Loading trigger...</div>;
 */
export const useIntersectionObserver = ({
  onIntersect,
  enabled = true,
  threshold = 1.0,
  rootMargin = '0px',
}: UseIntersectionObserverProps) => {
  const observerRef = useRef<HTMLDivElement | null>(null);

  // Wrap onIntersect in useCallback to prevent unnecessary observer recreations
  const handleIntersect = useCallback(onIntersect, [onIntersect]);

  useEffect(() => {
    const currentElement = observerRef.current;

    if (!enabled || !currentElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleIntersect();
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    observer.observe(currentElement);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersect, enabled, threshold, rootMargin]);

  return observerRef;
};
